import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { useData } from '../contexts/DataContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPlus, FiTrendingUp, FiTrash2, FiBarChart3, FiAlertCircle } = FiIcons;

const GrowthTracker = () => {
  const { data, addGrowthData, deleteEntry } = useData();
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    averageWeight: '',
    averageLength: '',
    totalFish: '',
    fishLosses: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    addGrowthData({
      averageWeight: parseFloat(formData.averageWeight),
      averageLength: parseFloat(formData.averageLength),
      totalFish: parseInt(formData.totalFish),
      fishLosses: parseInt(formData.fishLosses) || 0
    });
    setFormData({
      averageWeight: '',
      averageLength: '',
      totalFish: '',
      fishLosses: ''
    });
    setShowForm(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const SimpleChart = ({ data }) => {
    if (!data || data.length === 0) return null;

    const maxWeight = Math.max(...data.map(d => d.averageWeight));
    const minWeight = Math.min(...data.map(d => d.averageWeight));
    const range = maxWeight - minWeight || 1;

    return (
      <div className="w-full h-64 bg-gray-50 rounded-lg p-4 relative">
        <h3 className="text-center font-semibold mb-4">Growth Trend</h3>
        <div className="relative h-40">
          <svg className="w-full h-full">
            {data.map((point, index) => {
              const x = (index / (data.length - 1)) * 100;
              const y = 100 - ((point.averageWeight - minWeight) / range) * 100;
              const nextPoint = data[index + 1];

              return (
                <g key={index}>
                  <circle
                    cx={`${x}%`}
                    cy={`${y}%`}
                    r="4"
                    fill="#14b8a6"
                    className="hover:r-6 transition-all"
                  />
                  {nextPoint && (
                    <line
                      x1={`${x}%`}
                      y1={`${y}%`}
                      x2={`${((index + 1) / (data.length - 1)) * 100}%`}
                      y2={`${100 - ((nextPoint.averageWeight - minWeight) / range) * 100}%`}
                      stroke="#14b8a6"
                      strokeWidth="2"
                    />
                  )}
                </g>
              );
            })}
          </svg>
        </div>
        <div className="flex justify-between text-xs text-gray-500 mt-2">
          <span>{minWeight}g</span>
          <span>{maxWeight}g</span>
        </div>
      </div>
    );
  };

  const getLatestGrowthData = () => {
    return data.growthData.length > 0
      ? data.growthData[data.growthData.length - 1]
      : null;
  };

  const latestData = getLatestGrowthData();
  const estimatedBiomass = latestData
    ? (latestData.averageWeight * latestData.totalFish) / 1000
    : 0;

  return (
    <div className="p-4 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Growth Tracker</h1>
          <p className="text-gray-600 mt-1">Monitor fish growth and biomass</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center space-x-2 bg-purple-500 text-white px-4 py-2 rounded-lg hover:bg-purple-600 transition-colors"
        >
          <SafeIcon icon={FiPlus} className="w-4 h-4" />
          <span>Add Data</span>
        </button>
      </div>
      
      {/* Educational Tip */}
      <div className="bg-purple-50 border-l-4 border-purple-500 p-3 rounded">
        <div className="flex">
          <div className="flex-shrink-0">
            <SafeIcon icon={FiAlertCircle} className="h-5 w-5 text-purple-500" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-purple-800">Growth Measurement Tip</h3>
            <div className="mt-1 text-sm text-purple-700">
              Measure at least 30 random fish to get an accurate average weight. For length measurement, 
              measure from the tip of the snout to the end of the tail. Tilapia typically grow 1-2g per day 
              under optimal conditions.
            </div>
          </div>
        </div>
      </div>

      {/* Growth Summary Cards */}
      <div className="grid grid-cols-2 gap-4">
        <motion.div
          whileHover={{ scale: 1.02 }}
          className="bg-white rounded-xl p-4 shadow-sm border border-gray-200"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Current Weight</p>
              <p className="text-2xl font-bold text-gray-900">
                {latestData?.averageWeight || 0}g
              </p>
            </div>
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiTrendingUp} className="w-5 h-5 text-purple-600" />
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">Market size: 400-600g</p>
        </motion.div>

        <motion.div
          whileHover={{ scale: 1.02 }}
          className="bg-white rounded-xl p-4 shadow-sm border border-gray-200"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Biomass</p>
              <p className="text-2xl font-bold text-gray-900">
                {estimatedBiomass.toFixed(1)}kg
              </p>
            </div>
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiBarChart3} className="w-5 h-5 text-green-600" />
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">Total weight of all fish combined</p>
        </motion.div>
      </div>
      
      {/* Expected Growth Guide */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">Tilapia Growth Milestones</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-2 px-2 font-medium text-gray-700">Age</th>
                <th className="text-left py-2 px-2 font-medium text-gray-700">Typical Weight</th>
                <th className="text-left py-2 px-2 font-medium text-gray-700">Typical Length</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-gray-100">
                <td className="py-2 px-2 text-gray-600">1 month</td>
                <td className="py-2 px-2 text-gray-600">5-10g</td>
                <td className="py-2 px-2 text-gray-600">3-5cm</td>
              </tr>
              <tr className="border-b border-gray-100">
                <td className="py-2 px-2 text-gray-600">2 months</td>
                <td className="py-2 px-2 text-gray-600">30-50g</td>
                <td className="py-2 px-2 text-gray-600">8-10cm</td>
              </tr>
              <tr className="border-b border-gray-100">
                <td className="py-2 px-2 text-gray-600">4 months</td>
                <td className="py-2 px-2 text-gray-600">100-150g</td>
                <td className="py-2 px-2 text-gray-600">15-18cm</td>
              </tr>
              <tr className="border-b border-gray-100">
                <td className="py-2 px-2 text-gray-600">6 months</td>
                <td className="py-2 px-2 text-gray-600">250-350g</td>
                <td className="py-2 px-2 text-gray-600">20-25cm</td>
              </tr>
              <tr>
                <td className="py-2 px-2 text-gray-600">8 months</td>
                <td className="py-2 px-2 text-gray-600">400-600g</td>
                <td className="py-2 px-2 text-gray-600">25-30cm</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="text-xs text-gray-500 mt-3 italic">Note: Growth rates may vary based on species, water quality, temperature, and feeding regime.</p>
      </div>

      {/* Growth Chart */}
      {data.growthData.length > 1 && (
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <SimpleChart data={data.growthData} />
        </div>
      )}

      {/* Add Growth Data Form */}
      {showForm && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl p-6 shadow-sm border border-gray-200"
        >
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Record Growth Data</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Average Weight (grams)
                </label>
                <input
                  type="number"
                  name="averageWeight"
                  value={formData.averageWeight}
                  onChange={handleInputChange}
                  step="0.1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Average Length (cm)
                </label>
                <input
                  type="number"
                  name="averageLength"
                  value={formData.averageLength}
                  onChange={handleInputChange}
                  step="0.1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Total Fish Alive
                </label>
                <input
                  type="number"
                  name="totalFish"
                  value={formData.totalFish}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fish Losses (if any)
                </label>
                <input
                  type="number"
                  name="fishLosses"
                  value={formData.fishLosses}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="0"
                />
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                type="submit"
                className="flex-1 bg-purple-500 text-white py-2 px-4 rounded-lg hover:bg-purple-600 transition-colors"
              >
                Save Data
              </button>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="flex-1 bg-gray-500 text-white py-2 px-4 rounded-lg hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </motion.div>
      )}

      {/* Growth History */}
      <div className="space-y-4">
        {data.growthData.length > 0 ? (
          [...data.growthData].reverse().map((growthData) => (
            <motion.div
              key={growthData.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl p-4 shadow-sm border border-gray-200"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <SafeIcon icon={FiTrendingUp} className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">
                      {format(new Date(growthData.date), 'MMM dd, yyyy')}
                    </h3>
                    <p className="text-sm text-gray-500">Growth Measurement</p>
                  </div>
                </div>
                <button
                  onClick={() => deleteEntry('growthData', growthData.id)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                </button>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Avg Weight</span>
                  <span className="text-lg font-semibold text-gray-900">{growthData.averageWeight}g</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Avg Length</span>
                  <span className="text-lg font-semibold text-gray-900">{growthData.averageLength}cm</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Total Fish</span>
                  <span className="text-lg font-semibold text-gray-900">{growthData.totalFish}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Biomass</span>
                  <span className="text-lg font-semibold text-gray-900">
                    {((growthData.averageWeight * growthData.totalFish) / 1000).toFixed(1)}kg
                  </span>
                </div>
                {growthData.fishLosses > 0 && (
                  <div className="col-span-2 flex justify-between items-center">
                    <span className="text-sm text-red-600">Fish Losses</span>
                    <span className="text-lg font-semibold text-red-600">{growthData.fishLosses}</span>
                  </div>
                )}
              </div>
            </motion.div>
          ))
        ) : (
          <div className="text-center py-12">
            <SafeIcon icon={FiTrendingUp} className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No growth data yet</h3>
            <p className="text-gray-500 mb-6">Start tracking your fish growth by adding your first measurement</p>
            <button
              onClick={() => setShowForm(true)}
              className="inline-flex items-center space-x-2 bg-purple-500 text-white px-6 py-3 rounded-lg hover:bg-purple-600 transition-colors"
            >
              <SafeIcon icon={FiPlus} className="w-5 h-5" />
              <span>Add First Measurement</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default GrowthTracker;